import { useState } from "react";
import { X, Upload } from "lucide-react";

const unitMeasurements = [
  "Per Chair",
  "Per Table",
  "Per Piece",
  "Per Unit",
  "Per Item",
  "Per Set",
  "Per Yard",
  "Per Square Foot",
  "Custom",
];

export default function QuickAddCategoryModal({ isOpen, onClose, onSave }) {
  const [name, setName] = useState("");
  const [unitMeasurement, setUnitMeasurement] = useState("");
  const [customUnitMeasurement, setCustomUnitMeasurement] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  if (!isOpen) return null;

  const handleSave = () => {
    if (name.trim() && unitMeasurement) {
      onSave({
        id: Date.now(),
        name: name.trim(),
        unitMeasurement: unitMeasurement === "Custom" ? customUnitMeasurement : unitMeasurement,
        image,
      });
      setName("");
      setUnitMeasurement("");
      setCustomUnitMeasurement("");
      setImage(null);
      setImagePreview("");
      onClose();
    }
  };

  const handleClose = () => {
    setName("");
    setUnitMeasurement("");
    setCustomUnitMeasurement("");
    setImage(null);
    setImagePreview("");
    onClose();
  };

  const handleImageUpload = (file) => {
    if (file && file.size <= 5 * 1024 * 1024) { // Max size 5MB
      const reader = new FileReader();
      reader.onload = () => {
        setImage(file);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert("File size exceeds 5MB or invalid file type.");
    }
  };

  const handleImageRemove = () => {
    setImage(null);
    setImagePreview("");
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-in fade-in duration-300">
      <div className="bg-white dark:bg-[#1E1E1E] rounded-[2rem] w-full max-w-md p-8 shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A]">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#111111] dark:text-white font-poppins">
            New Category
          </h3>
          <button
            onClick={handleClose}
            className="p-2 rounded-xl hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-all active:scale-90"
          >
            <X size={20} className="text-[#666666] dark:text-[#A0A0A0]" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-[#111111] dark:text-white font-inter mb-2">
              Category Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Furniture"
              className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all text-sm font-inter"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#111111] dark:text-white font-inter mb-2">
              Unit Measurement *
            </label>
            <div className="relative">
              <select
                value={unitMeasurement}
                onChange={(e) => setUnitMeasurement(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all text-sm font-inter appearance-none"
              >
                <option value="">Select unit measurement</option>
                {unitMeasurements.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {unitMeasurement === "Custom" && (
          <div className="overflow-hidden transition-all duration-300 ease-in-out">
            <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter mb-2">
              Custom Unit Measurement *
            </label>
            <input
              type="text"
              value={customUnitMeasurement}
              onChange={(e) => setCustomUnitMeasurement(e.target.value)}
              placeholder="e.g., Per Bundle, Per Package"
              autoFocus
              className="w-full px-4 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white placeholder-[#666666] dark:placeholder-[#A0A0A0] focus:outline-none focus:ring-2 focus:ring-[#62397f] dark:focus:ring-[#7C3AED] transition-all text-sm font-inter"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter mb-2">
            Category Image
          </label>
          {imagePreview ? (
            <div className="relative">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-40 object-cover rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A]"
              />
              <button
                onClick={handleImageRemove}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 hover:scale-110 active:scale-95 shadow-lg"
                title="Delete image"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] rounded-lg cursor-pointer hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
              <Upload
                size={24}
                className="text-[#666666] dark:text-[#A0A0A0] mb-2"
              />
              <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter mb-1">
                Click to upload image
              </span>
              <span className="text-xs text-[#999999] dark:text-[#808080] font-inter">
                PNG, JPG, JPEG, GIF, WEBP (Max 5MB)
              </span>
              <input
                type="file"
                accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                onChange={(e) => handleImageUpload(e.target.files[0])}
                className="hidden"
              />
            </label>
          )}
        </div>

        <div className="flex items-center gap-3 mt-8">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text:white rounded-xl font-bold text-sm font-inter transition-all hover:bg-[#F9FAFB] dark:hover:bg-[#262626] active:scale-95"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || !unitMeasurement || (unitMeasurement === "Custom" && !customUnitMeasurement.trim())}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white rounded-xl font-bold text-sm font-inter transition-all shadow-lg shadow-[#62397f]/20 hover:shadow-xl hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Category
          </button>
        </div>
      </div>
    </div>
  );
}
